import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Package, Search, Plus, Trash2, Minus } from "lucide-react";
import { formatDataItaliana } from "@/utils/dateUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PoseMagazzinoPage({ user, logout }) {
  const [merceArrivata, setMerceArrivata] = useState([]);
  const [scorte, setScorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroClienteMerce, setFiltroClienteMerce] = useState("");
  const [filtroArticoloMerce, setFiltroArticoloMerce] = useState("");
  const [filtroStatoMerce, setFiltroStatoMerce] = useState("all");
  
  // Form nuova scorta
  const [nuovaScorta, setNuovaScorta] = useState({
    articolo: "",
    quantita: "",
    colore: "",
    magazzino: "",
    note: ""
  });
  
  // Filtri scorte
  const [filtroScorteNome, setFiltroScorteNome] = useState("");
  const [filtroScorteColore, setFiltroScorteColore] = useState("");
  
  // Stato per gli input "Utilizzati" per ogni scorta
  const [utilizzatiInputs, setUtilizzatiInputs] = useState({});

  const fetchMerceArrivata = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/articoli-commessa/merce-arrivata`, { withCredentials: true });
      setMerceArrivata(res.data);
    } catch (e) {
      console.error("Error fetching merce arrivata:", e);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchScorte = async () => {
    try {
      const res = await axios.get(`${API}/scorte-magazzino`, { withCredentials: true });
      setScorte(res.data);
    } catch (e) {
      console.error("Error fetching scorte:", e);
      toast.error("Errore nel caricamento delle scorte");
    }
  };

  useEffect(() => {
    fetchMerceArrivata();
    fetchScorte();
  }, []);

  const handleApplicaFiltriMerce = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroClienteMerce) params.append('cliente_nome', filtroClienteMerce);
      if (filtroArticoloMerce) params.append('articolo_nome', filtroArticoloMerce);
      
      const res = await axios.get(`${API}/articoli-commessa/merce-arrivata?${params.toString()}`, { withCredentials: true });
      setMerceArrivata(res.data);
    } catch (e) {
      console.error("Error filtering merce arrivata:", e);
      toast.error("Errore nel filtraggio");
    }
  };

  const handleResetFiltriMerce = () => {
    setFiltroClienteMerce("");
    setFiltroArticoloMerce("");
    fetchMerceArrivata();
  };

  const handleAggiungiScorta = async (e) => {
    e.preventDefault();
    
    if (!nuovaScorta.articolo || !nuovaScorta.quantita) {
      toast.error("Articolo e Quantità sono obbligatori");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("articolo", nuovaScorta.articolo);
      formData.append("quantita", nuovaScorta.quantita);
      formData.append("colore", nuovaScorta.colore);
      formData.append("magazzino", nuovaScorta.magazzino);
      formData.append("note", nuovaScorta.note);

      await axios.post(`${API}/scorte-magazzino`, formData, { withCredentials: true });
      
      toast.success("Scorta aggiunta con successo");
      setNuovaScorta({ articolo: "", quantita: "", colore: "", magazzino: "", note: "" });
      fetchScorte();
    } catch (e) {
      console.error("Error adding scorta:", e);
      toast.error("Errore nell'aggiunta della scorta");
    }
  };

  const handleDeleteScorta = async (scortaId) => {
    if (!confirm("Sei sicuro di voler eliminare questa scorta?")) return;

    try {
      await axios.delete(`${API}/scorte-magazzino/${scortaId}`, { withCredentials: true });
      toast.success("Scorta eliminata");
      fetchScorte();
    } catch (e) {
      console.error("Error deleting scorta:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  // Funzione per applicare l'utilizzo (sottrarre dalla quantità)
  const handleApplicaUtilizzo = async (scorta) => {
    const quantitaUtilizzata = parseInt(utilizzatiInputs[scorta.id] || 0);
    
    if (!quantitaUtilizzata || quantitaUtilizzata <= 0) {
      toast.error("Inserisci una quantità valida da utilizzare");
      return;
    }
    
    if (quantitaUtilizzata > scorta.quantita) {
      toast.error("La quantità utilizzata non può superare la disponibilità");
      return;
    }
    
    const nuovaQuantita = scorta.quantita - quantitaUtilizzata;
    
    try {
      const formData = new FormData();
      formData.append("articolo", scorta.articolo);
      formData.append("quantita", nuovaQuantita);
      formData.append("colore", scorta.colore || "");
      formData.append("magazzino", scorta.magazzino || "");
      formData.append("note", scorta.note || "");
      
      await axios.put(`${API}/scorte-magazzino/${scorta.id}`, formData, { withCredentials: true });
      
      toast.success(`Utilizzati ${quantitaUtilizzata} unità. Nuova disponibilità: ${nuovaQuantita}`);
      
      // Reset input utilizzati per questa scorta
      setUtilizzatiInputs(prev => ({ ...prev, [scorta.id]: "" }));
      
      fetchScorte();
    } catch (e) {
      console.error("Error updating scorta:", e);
      toast.error("Errore nell'aggiornamento della quantità");
    }
  };

  // Filtra le scorte in base ai filtri
  const scorteFiltrate = scorte.filter(scorta => {
    const matchNome = !filtroScorteNome || 
      scorta.articolo.toLowerCase().includes(filtroScorteNome.toLowerCase());
    const matchColore = !filtroScorteColore || 
      (scorta.colore && scorta.colore.toLowerCase().includes(filtroScorteColore.toLowerCase()));
    return matchNome && matchColore;
  });

  // Filtra la merce in base allo stato
  const merceFiltrata = merceArrivata.filter(merce => {
    if (filtroStatoMerce === "all") return true;
    if (filtroStatoMerce === "da_programmare") {
      // Filtra merce con status_posa che contiene "da programmare" (case insensitive)
      return merce.status_posa && merce.status_posa.toLowerCase().includes("da programmare");
    }
    return true;
  });

  // Conta la merce da programmare
  const contaMerceDaProgrammare = merceArrivata.filter(m => 
    m.status_posa && m.status_posa.toLowerCase().includes("da programmare")
  ).length;

  return (
    <PageLayout user={user} logout={logout} testId="pose-magazzino-page" className="overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-slate-900 mb-1">Magazzino</h1>
              <p className="text-slate-600 text-sm hidden lg:block">Gestione merce arrivata e scorte di magazzino</p>
            </div>
          </div>

          <Tabs defaultValue="merce" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
              <TabsTrigger value="merce">Merce Arrivata</TabsTrigger>
              <TabsTrigger value="scorte">Scorte Magazzino</TabsTrigger>
            </TabsList>

            {/* Tab Merce Arrivata */}
            <TabsContent value="merce">
              <Card>
                <CardHeader>
                  <CardTitle>Merce Arrivata da Ordini</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Tab Filtro Stato */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={filtroStatoMerce === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFiltroStatoMerce("all")}
                      className={filtroStatoMerce === "all" ? "bg-slate-700 hover:bg-slate-800" : ""}
                    >
                      Tutta la Merce
                      <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                        filtroStatoMerce === "all" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
                      }`}>
                        {merceArrivata.length}
                      </span>
                    </Button>
                    <Button
                      variant={filtroStatoMerce === "da_programmare" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFiltroStatoMerce("da_programmare")}
                      className={filtroStatoMerce === "da_programmare" ? "bg-orange-600 hover:bg-orange-700" : ""}
                    >
                      Merce da Posare
                      <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${
                        filtroStatoMerce === "da_programmare" ? "bg-white/20 text-white" : "bg-orange-200 text-orange-700"
                      }`}>
                        {contaMerceDaProgrammare}
                      </span>
                    </Button>
                  </div>

                  {/* Filtri */}
                  <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="flex-1">
                      <Label>Filtra per Cliente</Label>
                      <Input
                        placeholder="Nome cliente..."
                        value={filtroClienteMerce}
                        onChange={(e) => setFiltroClienteMerce(e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Label>Filtra per Articolo</Label>
                      <Input
                        placeholder="Nome articolo..."
                        value={filtroArticoloMerce}
                        onChange={(e) => setFiltroArticoloMerce(e.target.value)}
                      />
                    </div>
                    <Button onClick={handleApplicaFiltriMerce} className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                      <Search className="w-4 h-4 mr-2" />
                      Applica Filtri
                    </Button>
                    <Button onClick={handleResetFiltriMerce} variant="outline">
                      Reset
                    </Button>
                  </div>
                  
                  {loading ? (
                    <p className="text-center py-8 text-slate-500">Caricamento...</p>
                  ) : merceFiltrata.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">
                      {filtroStatoMerce === "da_programmare" 
                        ? "Nessuna merce da programmare" 
                        : "Nessuna merce arrivata trovata"}
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white rounded-lg">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Data Arrivo</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Cliente</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Articolo</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Magazzino</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Giorni Deposito</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Status Posa</th>
                            <th className="text-center p-3 text-sm font-semibold text-slate-700">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {merceFiltrata.map((merce, idx) => (
                            <tr 
                              key={idx}
                              className={`border-b hover:bg-slate-50 transition-colors ${
                                merce.alert ? 'bg-red-50' : ''
                              }`}
                            >
                              <td className="p-3 text-sm">{formatDataItaliana(merce.data_arrivo)}</td>
                              <td className="p-3 text-sm font-medium">{merce.cliente_nome} {merce.cliente_cognome}</td>
                              <td className="p-3 text-sm">{merce.articolo}</td>
                              <td className="p-3">
                                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                                  {merce.deposito || "Non specificato"}
                                </span>
                              </td>
                              <td className="p-3">
                                <span className={`font-medium text-sm ${merce.alert ? 'text-red-600' : 'text-slate-700'}`}>
                                  {merce.giorni_deposito} {merce.giorni_deposito === 1 ? 'giorno' : 'giorni'}
                                </span>
                              </td>
                              <td className="p-3 text-sm">{merce.status_posa}</td>
                              <td className="p-3 text-center">
                                <Link to={`/commesse-pose/${merce.commessa_id}`}>
                                  <Button size="sm" variant="outline">
                                    Vai a Commessa
                                  </Button>
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab Scorte Magazzino */}
            <TabsContent value="scorte">
              <div className="space-y-6">
                {/* Form Aggiungi Scorta */}
                <Card>
                  <CardHeader>
                    <CardTitle>Aggiungi Nuova Scorta</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAggiungiScorta} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Articolo *</Label>
                          <Input
                            value={nuovaScorta.articolo}
                            onChange={(e) => setNuovaScorta({...nuovaScorta, articolo: e.target.value})}
                            placeholder="Nome articolo"
                            required
                          />
                        </div>
                        <div>
                          <Label>Quantità *</Label>
                          <Input
                            type="number"
                            value={nuovaScorta.quantita}
                            onChange={(e) => setNuovaScorta({...nuovaScorta, quantita: e.target.value})}
                            placeholder="Es: 10"
                            required
                          />
                        </div>
                        <div>
                          <Label>Colore</Label>
                          <Input
                            value={nuovaScorta.colore}
                            onChange={(e) => setNuovaScorta({...nuovaScorta, colore: e.target.value})}
                            placeholder="Es: Bianco"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Magazzino</Label>
                          <Input
                            value={nuovaScorta.magazzino}
                            onChange={(e) => setNuovaScorta({...nuovaScorta, magazzino: e.target.value})}
                            placeholder="Es: Magazzino A"
                          />
                        </div>
                        <div>
                          <Label>Note</Label>
                          <Textarea
                            value={nuovaScorta.note}
                            onChange={(e) => setNuovaScorta({...nuovaScorta, note: e.target.value})}
                            placeholder="Note aggiuntive..."
                            rows={1}
                          />
                        </div>
                      </div>
                      <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi Scorta
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Tabella Scorte */}
                <Card>
                  <CardHeader>
                    <CardTitle>Elenco Scorte ({scorteFiltrate.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Filtri Scorte */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <Label>Cerca per Nome Articolo</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Cerca articolo..."
                            value={filtroScorteNome}
                            onChange={(e) => setFiltroScorteNome(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <Label>Cerca per Colore</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Cerca colore..."
                            value={filtroScorteColore}
                            onChange={(e) => setFiltroScorteColore(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      {(filtroScorteNome || filtroScorteColore) && (
                        <div className="flex items-end">
                          <Button 
                            variant="outline" 
                            onClick={() => { setFiltroScorteNome(""); setFiltroScorteColore(""); }}
                          >
                            Reset Filtri
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {scorteFiltrate.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-600">
                          {scorte.length === 0 ? "Nessuna scorta inserita" : "Nessun risultato per i filtri applicati"}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full bg-white rounded-lg">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left p-3 text-sm font-semibold text-slate-700">Articolo</th>
                              <th className="text-center p-3 text-sm font-semibold text-slate-700">Quantità</th>
                              <th className="text-center p-3 text-sm font-semibold text-slate-700 bg-orange-50">Utilizzati</th>
                              <th className="text-left p-3 text-sm font-semibold text-slate-700">Colore</th>
                              <th className="text-left p-3 text-sm font-semibold text-slate-700">Magazzino</th>
                              <th className="text-left p-3 text-sm font-semibold text-slate-700">Note</th>
                              <th className="text-center p-3 text-sm font-semibold text-slate-700">Cancella</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scorteFiltrate.map((scorta) => (
                              <tr key={scorta.id} className="border-b hover:bg-slate-50">
                                <td className="p-3 text-sm font-medium">{scorta.articolo}</td>
                                <td className="p-3 text-center">
                                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    scorta.quantita <= 5 
                                      ? 'bg-red-100 text-red-700' 
                                      : scorta.quantita <= 20 
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {scorta.quantita}
                                  </span>
                                </td>
                                <td className="p-3 bg-orange-50/50">
                                  <div className="flex items-center gap-2 justify-center">
                                    <Input
                                      type="number"
                                      min="1"
                                      max={scorta.quantita}
                                      value={utilizzatiInputs[scorta.id] || ""}
                                      onChange={(e) => setUtilizzatiInputs(prev => ({
                                        ...prev,
                                        [scorta.id]: e.target.value
                                      }))}
                                      placeholder="0"
                                      className="w-20 text-center"
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleApplicaUtilizzo(scorta)}
                                      disabled={!utilizzatiInputs[scorta.id]}
                                      className="bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
                                      title="Sottrai dalla quantità"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                                <td className="p-3 text-sm">{scorta.colore || "-"}</td>
                                <td className="p-3">
                                  <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                                    {scorta.magazzino || "Non specificato"}
                                  </span>
                                </td>
                                <td className="p-3 text-sm text-slate-600">{scorta.note || "-"}</td>
                                <td className="p-3 text-center">
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteScorta(scorta.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
    </PageLayout>
  );
}
